import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { ErroApi } from '../../application/portas';
import { Marca } from '../design-system/Marca';

type Props = {
  entrar: (email: string, senha: string) => Promise<void>;
  erroInicial?: string;
  /** Carrega o reCAPTCHA silencioso ao abrir a tela (RQ-ACE-017). */
  preparar?: () => void;
};

/** Logotipo do Google nas cores oficiais (exigência da marca para o botão de login). */
export function LogoGoogle() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const MENSAGENS: Record<number, string> = {
  401: 'E-mail ou senha inválidos.',
  403: 'Não conseguimos confirmar o acesso. Recarregue a página e tente de novo.',
};

/** O Google exige o aviso quando o selo flutuante do reCAPTCHA fica escondido (RQ-ACE-017). */
function AvisoRecaptcha() {
  return (
    <p className="cartao-acesso__recaptcha">
      Protegido pelo reCAPTCHA do Google (<a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">privacidade</a> e{' '}
      <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">termos</a>).
    </p>
  );
}

/** Tela de login (RQ-ACE-001, RQ-ACE-002): e-mail e senha ou Google, só para quem já está cadastrado. */
export function TelaEntrar({ entrar, erroInicial, preparar }: Props) {
  const [email, definirEmail] = useState('');
  const [senha, definirSenha] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState(erroInicial);
  useEffect(() => preparar?.(), [preparar]);

  const aoEnviar = async (evento: FormEvent) => {
    evento.preventDefault();
    definirEnviando(true);
    definirErro(undefined);
    try {
      await entrar(email, senha);
    } catch (e) {
      definirErro(MENSAGENS[e instanceof ErroApi ? e.status : 0] ?? 'Não foi possível entrar agora. Tente de novo em instantes.');
      definirEnviando(false);
    }
  };

  return (
    <main className="tela-acesso">
      <form className="cartao-acesso" onSubmit={aoEnviar} noValidate>
        <div className="cartao-acesso__marca">
          <Marca tamanho={40} />
          <span>Nexital</span>
        </div>
        <h1>Entrar</h1>
        <p className="cartao-acesso__subtitulo">Acompanhe as licitações da sua empresa.</p>

        <label className="campo">
          <span>E-mail</span>
          <input type="email" autoComplete="username" required value={email} onChange={(e) => definirEmail(e.target.value)} autoFocus />
        </label>
        <label className="campo">
          <span>Senha</span>
          <input type="password" autoComplete="current-password" required value={senha} onChange={(e) => definirSenha(e.target.value)} />
        </label>

        {erro && (
          <p className="cartao-acesso__erro" role="alert">
            {erro}
          </p>
        )}

        <button type="submit" className="botao-primario cartao-acesso__botao" disabled={enviando || !email || !senha}>
          {enviando && <Loader2 size={18} className="girando" aria-hidden />} Entrar
        </button>

        <div className="cartao-acesso__divisor">
          <span>ou</span>
        </div>

        <a className="botao-google" href="/api/auth/google">
          <LogoGoogle /> Entrar com Google
        </a>
        <AvisoRecaptcha />
      </form>
    </main>
  );
}
